'use client';

import { generateMiniProgramUrlLink } from '@/app/services/wechat';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { openUrl } from '@/lib/open-url';
import {
  WX_MINI_PROGRAM_BANNER_DISMISSED_KEY,
  WX_MINI_PROGRAM_HOME_PATH,
  shouldShowWxMiniProgramBanner,
} from '@/lib/wx-mini-program-banner';
import { XIcon } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export function WxMiniProgramBanner() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    const dismissed =
      window.localStorage.getItem(WX_MINI_PROGRAM_BANNER_DISMISSED_KEY) ===
      'true';

    setVisible(
      shouldShowWxMiniProgramBanner({
        userAgent: window.navigator.userAgent || '',
        dismissed,
      }),
    );
  }, []);

  const dismiss = () => {
    setVisible(false);
  };

  const openMiniProgram = async () => {
    setOpening(true);
    try {
      const res = await generateMiniProgramUrlLink(
        WX_MINI_PROGRAM_HOME_PATH,
        '',
      );
      if (res?.data) {
        openUrl(document, res.data);
        return;
      }
    } catch (error) {
      console.error('生成小程序首页链接失败', error);
    } finally {
      setOpening(false);
    }

    setOpen(true);
    toast.info('暂时无法直接拉起小程序，请扫码进入', {
      position: 'top-center',
    });
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <div className="border-b border-emerald-200/80 bg-linear-to-r from-emerald-50 via-lime-50 to-white text-emerald-950">
        <div className="mx-auto flex min-h-11 max-w-screen-2xl items-center gap-3 px-3 py-2 md:px-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center">
              <svg
                viewBox="0 0 1024 1024"
                aria-hidden="true"
                className="h-6 w-6"
              >
                <path
                  d="M512 0a512 512 0 1 0 512 512A512 512 0 0 0 512 0z m256.717 460.186a151.962 151.962 0 0 1-87.347 65.74 83.251 83.251 0 0 1-24.474 4.096 29.082 29.082 0 0 1 0-58.163 15.667 15.667 0 0 0 6.451-1.229 91.443 91.443 0 0 0 55.91-40.96 75.264 75.264 0 0 0 11.06-39.628c0-45.978-42.496-83.866-94.31-83.866a105.267 105.267 0 0 0-51.2 13.414 81.92 81.92 0 0 0-43.725 70.452v244.224a138.445 138.445 0 0 1-72.704 120.422 159.642 159.642 0 0 1-79.77 20.48c-84.378 0-153.6-63.488-153.6-142.029a136.192 136.192 0 0 1 19.763-69.837 151.962 151.962 0 0 1 87.347-65.74 85.914 85.914 0 0 1 24.474-4.096 29.082 29.082 0 1 1 0 58.163 15.667 15.667 0 0 0-6.451 1.229 95.949 95.949 0 0 0-55.91 40.96 75.264 75.264 0 0 0-11.06 39.628c0 45.978 42.496 83.866 94.925 83.866a105.267 105.267 0 0 0 51.2-13.414 81.92 81.92 0 0 0 43.622-70.452V390.35a138.752 138.752 0 0 1 72.807-120.525 151.245 151.245 0 0 1 79.155-21.504c84.378 0 153.6 63.488 153.6 142.029a136.192 136.192 0 0 1-19.763 69.837z"
                  fill="#00B240"
                />
              </svg>
            </div>
            <p className="min-w-0 text-sm leading-5 md:text-[13px]">
              推荐使用微信小程序，体验更稳定，下载和分享更方便。
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-emerald-300 bg-white/80 text-emerald-900 hover:bg-emerald-100"
              onClick={openMiniProgram}
              disabled={opening}
            >
              {opening ? '打开中...' : '打开小程序'}
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-emerald-900 hover:bg-emerald-100"
              onClick={dismiss}
              aria-label="关闭小程序推荐横幅"
            >
              <XIcon />
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle className="text-center">
              打开 Suppr 微信小程序
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 pb-2">
            <Image
              alt="Suppr 微信小程序码"
              width={240}
              height={240}
              src="/img/gh_21d610166629_430.jpg"
              className="rounded-2xl border"
              sizes="240px"
            />
            <p className="text-center text-sm text-muted-foreground">
              保存图片或使用另一台设备扫码，即可进入小程序。
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
